import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../context/NotificationContext';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import AdminPagination from '../../components/AdminPagination';
import './DiscountCodes.css';

const PAGINATION_MOBILE_BREAKPOINT = 768;
const ITEMS_PER_PAGE_DESKTOP = 10;
const ITEMS_PER_PAGE_MOBILE = 5;

const DiscountCodes = () => {
  const { t } = useTranslation();
  const { showError } = useNotification();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    type: 'percentage',
    value: 0,
    minOrderAmount: 0,
    maxDiscount: null,
    restaurant: null,
    restaurantType: null,
    validFrom: new Date().toISOString().split('T')[0],
    validUntil: null,
    usageLimit: null,
    userLimit: 1,
    description: ''
  });
  const [restaurants, setRestaurants] = useState([]);
  const [stores, setStores] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE_DESKTOP);

  useEffect(() => {
    fetchCodes();
    fetchRestaurants();
    fetchStores();
  }, []);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${PAGINATION_MOBILE_BREAKPOINT}px)`);
    const handler = () => setItemsPerPage(mql.matches ? ITEMS_PER_PAGE_MOBILE : ITEMS_PER_PAGE_DESKTOP);
    handler();
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const totalPages = Math.max(1, Math.ceil(codes.length / itemsPerPage));
  const paginatedCodes = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return codes.slice(start, start + itemsPerPage);
  }, [codes, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [codes.length, itemsPerPage]);

  const fetchCodes = async () => {
    try {
      const response = await axios.get(`${API_URL}/discount-codes`);
      setCodes(response.data);
    } catch (error) {
      console.error('Error fetching discount codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRestaurants = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/restaurants`);
      setRestaurants(response.data);
    } catch (error) {
      console.error('Error fetching restaurants:', error);
    }
  };

  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/stores`);
      setStores(response.data);
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCode) {
        await axios.patch(`${API_URL}/discount-codes/${editingCode._id}`, formData);
      } else {
        await axios.post(`${API_URL}/discount-codes`, formData);
      }
      setShowModal(false);
      setEditingCode(null);
      resetForm();
      await fetchCodes();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to save discount code');
    }
  };

  const handleEdit = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      type: code.type,
      value: code.value,
      minOrderAmount: code.minOrderAmount || 0,
      maxDiscount: code.maxDiscount || null,
      restaurant: code.restaurant?._id || null,
      restaurantType: code.restaurantType || null,
      validFrom: code.validFrom ? new Date(code.validFrom).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      validUntil: code.validUntil ? new Date(code.validUntil).toISOString().split('T')[0] : null,
      usageLimit: code.usageLimit || null,
      userLimit: code.userLimit || 1,
      description: code.description || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.confirmDeleteCode'))) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/discount-codes/${id}`);
      await fetchCodes();
    } catch (error) {
      showError(t('admin.failedToDeleteCode'));
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      type: 'percentage',
      value: 0,
      minOrderAmount: 0,
      maxDiscount: null,
      restaurant: null,
      restaurantType: null,
      validFrom: new Date().toISOString().split('T')[0],
      validUntil: null,
      usageLimit: null,
      userLimit: 1,
      description: ''
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('admin.noExpiration');
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="discount-codes-container">
        <div className="loading-spinner">{t('admin.loadingDiscountCodes')}</div>
      </div>
    );
  }

  return (
    <div className="discount-codes-container">
      <div className="discount-codes-header">
        <h2>{t('admin.discountCodes')}</h2>
        <button className="btn-add-code" onClick={() => { setShowModal(true); setEditingCode(null); resetForm(); }}>
          <i className="fas fa-plus"></i> {t('admin.addDiscountCode')}
        </button>
      </div>

      <div className="codes-table-wrapper">
        <table className="codes-table">
          <thead>
            <tr>
              <th>{t('admin.code')}</th>
              <th>{t('admin.type')}</th>
              <th>{t('admin.value')}</th>
              <th>{t('admin.minOrder')}</th>
              <th>{t('admin.restaurant')}/{t('admin.store')}</th>
              <th>{t('admin.validUntil')}</th>
              <th>{t('admin.usage')}</th>
              <th>{t('admin.status')}</th>
              <th>{t('admin.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {codes.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-codes">{t('admin.noDiscountCodesFound')}</td>
              </tr>
            ) : (
              paginatedCodes.map(code => (
                <tr key={code._id}>
                  <td className="code-cell" data-label={t('admin.code')}><strong>{code.code}</strong></td>
                  <td data-label={t('admin.type')}>{code.type === 'percentage' ? t('admin.percentage') : t('admin.fixedAmount')}</td>
                  <td data-label={t('admin.value')}>
                    {code.type === 'percentage' ? `${code.value}%` : `€${code.value.toFixed(2)}`}
                    {code.maxDiscount && code.type === 'percentage' && (
                      <span className="max-discount"> (max €{code.maxDiscount.toFixed(2)})</span>
                    )}
                  </td>
                  <td data-label={t('admin.minOrder')}>€{code.minOrderAmount.toFixed(2)}</td>
                  <td data-label={`${t('admin.restaurant')}/${t('admin.store')}`}>{code.restaurant ? code.restaurant.name : t('admin.all')}</td>
                  <td data-label={t('admin.validUntil')}>{formatDate(code.validUntil)}</td>
                  <td data-label={t('admin.usage')}>
                    {code.usedCount || 0} / {code.usageLimit || '∞'}
                  </td>
                  <td data-label={t('admin.status')}>
                    <span className={`status-badge ${code.active ? 'active' : 'inactive'}`}>
                      {code.active ? t('admin.active') : t('admin.inactive')}
                    </span>
                  </td>
                  <td data-label={t('admin.actions')}>
                    <button onClick={() => handleEdit(code)} className="btn-edit">{t('admin.edit')}</button>
                    <button onClick={() => handleDelete(code._id)} className="btn-delete">{t('admin.delete')}</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AdminPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={codes.length}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
      />

      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setEditingCode(null); resetForm(); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingCode ? t('admin.editDiscountCode') : t('admin.addDiscountCode')}</h3>
              <button className="close-btn" onClick={() => { setShowModal(false); setEditingCode(null); resetForm(); }}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="discount-code-form">
              <div className="form-group">
                <label>{t('admin.code')} *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  required
                  placeholder="DISCOUNT10"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('admin.type')} *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    required
                  >
                    <option value="percentage">{t('admin.percentage')}</option>
                    <option value="fixed">{t('admin.fixedAmount')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('admin.value')} *</label>
                  <input
                    type="number"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) || 0 })}
                    required
                    min="0"
                    step="any"
                  />
                </div>
              </div>
              {formData.type === 'percentage' && (
                <div className="form-group">
                  <label>{t('admin.maxDiscount')} ({t('admin.optional')})</label>
                  <input
                    type="number"
                    value={formData.maxDiscount || ''}
                    onChange={(e) => setFormData({ ...formData, maxDiscount: e.target.value ? parseFloat(e.target.value) : null })}
                    min="0"
                    step="any"
                    placeholder={t('admin.unlimited')}
                  />
                </div>
              )}
              <div className="form-group">
                <label>{t('admin.minimumOrderAmount')}</label>
                <input
                  type="number"
                  value={formData.minOrderAmount}
                  onChange={(e) => setFormData({ ...formData, minOrderAmount: parseFloat(e.target.value) || 0 })}
                  min="0"
                  step="any"
                />
              </div>
              <div className="form-group">
                <label>{t('admin.appliesTo')}</label>
                <select
                  value={formData.restaurantType || 'all'}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'all') {
                      setFormData({ ...formData, restaurant: null, restaurantType: null });
                    } else {
                      setFormData({ ...formData, restaurantType: value, restaurant: null });
                    }
                  }}
                >
                  <option value="all">{t('admin.allRestaurantsStores')}</option>
                  <option value="Restaurant">{t('admin.specificRestaurant')}</option>
                  <option value="Store">{t('admin.specificStore')}</option>
                </select>
              </div>
              {formData.restaurantType && (
                <div className="form-group">
                  <label>{t('admin.select')} {formData.restaurantType === 'Restaurant' ? t('admin.restaurant') : t('admin.store')}</label>
                  <select
                    value={formData.restaurant || ''}
                    onChange={(e) => setFormData({ ...formData, restaurant: e.target.value || null })}
                  >
                    <option value="">{t('admin.select')}</option>
                    {(formData.restaurantType === 'Restaurant' ? restaurants : stores).map(item => (
                      <option key={item._id} value={item._id}>{item.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label>{t('admin.validFrom')}</label>
                  <input
                    type="date"
                    value={formData.validFrom}
                    onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.validUntil')} ({t('admin.optional')})</label>
                  <input
                    type="date"
                    value={formData.validUntil || ''}
                    onChange={(e) => setFormData({ ...formData, validUntil: e.target.value || null })}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('admin.usageLimit')} ({t('admin.optional')})</label>
                  <input
                    type="number"
                    value={formData.usageLimit || ''}
                    onChange={(e) => setFormData({ ...formData, usageLimit: e.target.value ? parseInt(e.target.value) : null })}
                    min="1"
                    placeholder={t('admin.unlimited')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.userLimit')}</label>
                  <input
                    type="number"
                    value={formData.userLimit}
                    onChange={(e) => setFormData({ ...formData, userLimit: parseInt(e.target.value) || 1 })}
                    min="1"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>{t('admin.description')} ({t('admin.optional')})</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  placeholder={t('admin.descriptionOfDiscount')}
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => { setShowModal(false); setEditingCode(null); resetForm(); }} className="btn-cancel">
                  {t('admin.cancel')}
                </button>
                <button type="submit" className="btn-submit">
                  {editingCode ? t('admin.update') : t('admin.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountCodes;
